// BankAccountProfileService.tsx
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { BankAccountProfileService } from '../../core/services';
import { PageSize } from '../../type';
import { AccountSummaryFilters, GetFinancesResponse } from './interface';

export const useGetBankAccountsService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-bankaccounts-data'],
      queryFn: async ({ pageParam }) => {
        return BankAccountProfileService.getBankAccounts({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          bankName: initialParams.bankName,
          accountNumber: initialParams.accountNumber,
          accountTitle: initialParams.accountTitle,
          type: initialParams.type,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetBankAccountsDropdownService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: [
        'get-bankaccounts-dropdown-data',
        initialParams.stationId,
        initialParams.paymentMode,
        initialParams.search,
        initialParams.pageNumber,
      ],
      queryFn: async ({ pageParam }) => {
        return BankAccountProfileService.getBankAccountsDropdown({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          bankName: initialParams.bankName,
          accountTitle: initialParams.accountTitle,
          search: initialParams.search,
          businessId: initialParams.businessId,
          stationId: initialParams.stationId,
          paymentMode: initialParams.paymentMode,
          active: initialParams.active ?? true,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetFinanceExpensesService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-expenses'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceExpenses({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFinanceAssetsService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-assets'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceAssets({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFinanceLiabilitiesService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-liabilities'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceLiabilities({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFinanceEquitiesService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-equities'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceEquities({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFinanceRevenueService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-revenue'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceRevenue({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetFinanceProfitLosService = (params?: AccountSummaryFilters) => {
  const { data, isLoading, error, refetch } = useQuery<GetFinancesResponse, unknown>({
    queryKey: ['get-finance-profit-loss'],
    queryFn: async () => {
      return BankAccountProfileService.getFinanceProfitLoss({
        ...params,
      });
    },
    enabled: false,
  });

  return {
    data,
    isLoading,
    error,
    refetch,
  };
};

export const useGetAccountLedgerByIDService = (id: number, initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-accounts-ledger-data'],
      queryFn: async ({ pageParam }) => {
        return BankAccountProfileService.getAccountLedgerByID(id, {
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};

export const useGetAccountTransfersService = (initialParams: PageSize) => {
  const { data, isLoading, error, refetch, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery({
      queryKey: ['get-accounts-transfer-data'],
      queryFn: async ({ pageParam }) => {
        return BankAccountProfileService.getAccountTransfers({
          page: initialParams.pageNumber ?? pageParam,
          size: initialParams.size,
          accountName: initialParams.accountName,
        });
      },
      initialPageParam: 1,
      getNextPageParam: (res) => (!res?.last ? res?.number + 1 : undefined),
      enabled: false,
      retry: 1,
      retryDelay: 36000000,
    });
  const isDataFetching = isFetching || isFetchingNextPage;

  return {
    data,
    isLoading,
    error,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isDataFetching,
  };
};
